import React from 'react';
import { 
  Box, 
  Divider, 
  Drawer as MuiDrawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Typography,
  Avatar
} from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PersonIcon from '@mui/icons-material/Person';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../models/User';
import { useTheme } from '../../contexts/ThemeContext';

interface DrawerProps {
  drawerWidth: number;
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
}

export const Drawer = ({ 
  drawerWidth, 
  mobileOpen, 
  handleDrawerToggle 
}: DrawerProps) => {
  const { user, userRole } = useAuth();
  const location = useLocation();
  const { darkMode } = useTheme();
  
  // Manager check based on userRole enum
  const isManager = userRole === UserRole.HotelManager;


  // Active route check
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const drawer = (
    <div>
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          background: darkMode 
            ? 'linear-gradient(135deg, #5a6c7d, #4a5a6b, #3c4954)'
            : '#ffffff',
          color: darkMode ? 'white' : '#0f172a'
        }}
      >
        <Box
          component="img"
          src="/rafilis-mp-logo.png"
          alt="Multizone Player Logo"
          sx={{ 
            width: 120,
            height: 'auto',
            mb: 1,
            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
          }}
        />
        {user && (
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1,
              fontFamily: 'Heebo, sans-serif'
            }}
          >
            {userRole === UserRole.HotelManager ? 'Hotel Manager' : 'Hotel Staff'}
          </Typography>
        )}
      </Box>
      
      <List sx={{ mt: 2, px: 2 }}>
        <ListItem disablePadding sx={{ mb: 1 }}>
          <ListItemButton 
            component={NavLink} 
            to="/zones"
            sx={{
              borderRadius: '10px',
              background: isActive('/zones') ? 'rgba(46, 134, 171, 0.1)' : 'transparent',
              transition: 'all 0.3s',
              '&:hover': {
                background: 'rgba(46, 134, 171, 0.1)',
                transform: 'translateX(5px)'
              }
            }}
          >
            <ListItemIcon sx={{ color: isActive('/zones') ? '#2e86ab' : 'inherit' }}>
              <MusicNoteIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Music Zones" 
              sx={{ 
                '& .MuiTypography-root': { 
                  fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                  fontWeight: isActive('/zones') ? 600 : 500,
                  color: isActive('/zones') ? '#2e86ab' : 'inherit'
                } 
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Hotel Manager sees all management options */}
        {isManager && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography 
              variant="caption" 
              sx={{ 
                px: 2, 
                py: 1, 
                display: 'block',
                color: 'text.secondary',
                fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                fontWeight: 500
              }}
            >
              MANAGEMENT
            </Typography>
            
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton 
                component={NavLink} 
                to="/users"
                sx={{
                  borderRadius: '10px',
                  background: isActive('/users') ? 'rgba(46, 134, 171, 0.1)' : 'transparent',
                  transition: 'all 0.3s',
                  '&:hover': {
                    background: 'rgba(46, 134, 171, 0.1)',
                    transform: 'translateX(5px)'
                  }
                }}
              >
                <ListItemIcon sx={{ color: isActive('/users') ? '#2e86ab' : 'inherit' }}>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Users" 
                  sx={{ 
                    '& .MuiTypography-root': { 
                      fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                      fontWeight: isActive('/users') ? 600 : 500,
                      color: isActive('/users') ? '#2e86ab' : 'inherit'
                    } 
                  }}
                />
              </ListItemButton>
            </ListItem>
          </>
        )}

      </List>
      
      
      {user && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            p: 2,
            borderTop: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.05)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              sx={{ 
                bgcolor: 'rgba(46, 134, 171, 0.1)', 
                color: '#2e86ab',
                width: 40,
                height: 40
              }}
            >
              {user.username.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ ml: 2 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                }}
              >
                {user.username}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  color: 'text.secondary',
                  fontFamily: 'Roboto, "Helvetica", "Arial", sans-serif',
                }}
              >
                {user.username || 'Staff'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      
    </div>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      {/* Mobile drawer */}
      <MuiDrawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: darkMode ? '#2f3239' : '#ffffff',
            boxShadow: darkMode 
              ? '0 0 20px rgba(0, 0, 0, 0.3)'
              : '0 0 20px rgba(0, 0, 0, 0.1)',
            borderRight: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)'
          },
        }}
      >
        {drawer}
      </MuiDrawer>
      
      {/* Desktop drawer */}
      <MuiDrawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: darkMode ? '#2f3239' : '#ffffff',
            boxShadow: darkMode 
              ? '0 0 20px rgba(0, 0, 0, 0.3)'
              : '0 0 20px rgba(0, 0, 0, 0.05)',
            borderRight: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)'
          },
        }}
        open
      >
        {drawer}
      </MuiDrawer>
    </Box>
  );
};